import { Box, Card, CardContent, Typography, Paper, Button } from "@mui/material"
import { Smartphone, Link, Phone, MessageCircle, FileText, Copy,
  Image as ImageIcon, Video as VideoIcon, FileText as FileIcon
 } from "lucide-react"

export default function WhatsAppPreview({ template, variableExamples }) {
  const getButtonIcon = (type) => {
    const iconStyle = { width: 14, height: 14 }
    switch (type) {
      case "URL":
        return <Link style={iconStyle} />
      case "PHONE_NUMBER":
        return <Phone style={iconStyle} />
      case "WHATSAPP":
        return <MessageCircle style={iconStyle} />
      case "FLOW":
        return <FileText style={iconStyle} />
      case "COPY_CODE":
        return <Copy style={iconStyle} />
      default:
        return null
    }
  }

  const renderFormattedText = (text) => {
    // Primero reemplazar variables usando variableExamples
    const textWithVars = text.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
      return variableExamples[variable] || match;
    });

    // Procesar el formato de WhatsApp
    const parts = [];
    let lastIndex = 0;

    // Regex para capturar: *bold*, _italic_, ~strikethrough~, ```monospace```
    const formatRegex = /(\*[^*]+\*)|(_[^_]+_)|(~[^~]+~)|(```[^`]+```)/g;
    let match;

    while ((match = formatRegex.exec(textWithVars)) !== null) {
      // Agregar texto sin formato antes del match
      if (match.index > lastIndex) {
        parts.push(textWithVars.substring(lastIndex, match.index));
      }

      const fullMatch = match[0];
      const content = fullMatch.slice(
        fullMatch.startsWith('```') ? 3 : 1,
        fullMatch.endsWith('```') ? -3 : -1
      );

      // Determinar el tipo de formato
      if (fullMatch.startsWith('*')) {
        parts.push(<strong key={match.index}>{content}</strong>);
      } else if (fullMatch.startsWith('_')) {
        parts.push(<em key={match.index}>{content}</em>);
      } else if (fullMatch.startsWith('~')) {
        parts.push(<del key={match.index}>{content}</del>);
      } else if (fullMatch.startsWith('```')) {
        parts.push(
          <code
            key={match.index}
            style={{
              fontFamily: 'monospace',
              color: '#000000'
            }}
          >
            {content}
          </code>
        );
      }

      lastIndex = match.index + fullMatch.length;
    }

    // Agregar el texto restante
    if (lastIndex < textWithVars.length) {
      parts.push(textWithVars.substring(lastIndex));
    }

    return parts.length > 0 ? parts : textWithVars;
  };

  return (
    <Card sx={{ position: "sticky", top: 24 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Smartphone style={{ width: 20, height: 20 }} />
          <Typography variant="h6">Vista previa</Typography>
        </Box>

        {/* Phone mockup */}
        <Paper
          elevation={3}
          sx={{
            bgcolor: "#ECE5DD",
            borderRadius: 4,
            p: 2,
            minHeight: 400,
          }}
        >
          {/* WhatsApp message bubble */}
          <Paper
            sx={{
              bgcolor: "#FFFFFF",
              borderTopLeftRadius: 0,
              borderTopRightRadius: 4,
              p: 2,
              maxWidth: "85%",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: -8,
                top: 0,
                width: 0,
                height: 0,
                borderStyle: "solid",
                borderWidth: "0 8px 8px 0",
                borderColor: "transparent #FFFFFF transparent transparent",
              },
            }}
          >
            {template.header && (
  <>
    {template.header === "text" && template.headerText && (
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        {template.headerText}
      </Typography>
    )}
    
    {template.header === "image" && (
      <Box
        sx={{
          width: "100%",
          height: 150,
          bgcolor: "rgba(0,0,0,0.05)",
          borderRadius: 1,
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ImageIcon style={{ width: 48, height: 48, opacity: 0.3 }} />
      </Box>
    )}
    
    {template.header === "video" && (
      <Box
        sx={{
          width: "100%",
          height: 150,
          bgcolor: "rgba(0,0,0,0.05)",
          borderRadius: 1,
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <VideoIcon style={{ width: 48, height: 48, opacity: 0.3 }} />
      </Box>
    )}
    
    {template.header === "document" && (
      <Box
        sx={{
          width: "100%",
          p: 2,
          bgcolor: "rgba(0,0,0,0.05)",
          borderRadius: 1,
          mb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <FileIcon style={{ width: 24, height: 24, opacity: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          Documento.pdf
        </Typography>
      </Box>
    )}
  </>
)}

            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderFormattedText(template.content)}
            </Typography>

            {template.footer && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {template.footer}
              </Typography>
            )}

            {template.buttons && template.buttons.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {template.buttons.map((button, index) => (
                  <Button
                    key={button.id}
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={getButtonIcon(button.type)}
                    sx={{
                      justifyContent: "center",
                      textTransform: "none",
                      borderColor: "#E0E0E0",
                      color: "#00A5F4",
                      fontSize: "0.875rem",
                      py: 0.75,
                      "&:hover": {
                        borderColor: "#00A5F4",
                        bgcolor: "rgba(0, 165, 244, 0.04)",
                      },
                    }}
                  >
                    {button.text || "Botón"}
                  </Button>
                ))}
              </Box>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "right", mt: 1, fontSize: "0.65rem" }}
            >
              {new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          </Paper>
        </Paper>
      </CardContent>
    </Card>
  )
}
